import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminRole, OwnPool, OwnPoolSettings, OwnSupplyAlert, OwnSupplyView } from "../api/types.js";
import { count, trx, utc } from "../format.js";
import {
  Address,
  Blank,
  Button,
  EmptyState,
  ErrorNote,
  Fact,
  Field,
  Note,
  Panel,
  Pill,
  ScreenHeader,
  ShortId,
  Spinner,
  StatRow,
  Table,
  Td,
  Th,
  Tile,
  inputClass,
} from "../ui.js";

/**
 * Own supply (docs/product/own-supply-console-spec.md, owner approved 2026-10-20): our own stake watched as a producer.
 * Every pool is separate, with its own screen and settings. Watch-only: nothing here signs or sends; the one write is
 * a pool's settings (editor, audited). Alerts show here only — none go to Telegram (owner decision 3).
 */

export type OwnSupplyPage = "overview" | "delegations" | "admission" | "economics" | "bandwidth" | "suppliers";

export const OWN_SUPPLY_VIEWS: ReadonlyArray<{ view: OwnSupplyPage; label: string }> = [
  { view: "overview", label: "Overview" },
  { view: "delegations", label: "Delegations" },
  { view: "admission", label: "Admission" },
  { view: "economics", label: "Economics" },
  { view: "bandwidth", label: "Bandwidth" },
  { view: "suppliers", label: "Suppliers" },
];

const pct = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? null : `${(value * 100).toFixed(digits)} %`;
const hours = (seconds: number | null | undefined) =>
  seconds === null || seconds === undefined ? null : `${(seconds / 3600).toFixed(1)} h`;
const sunUnit = (value: number | null | undefined) =>
  value === null || value === undefined ? null : `${value.toFixed(2)} SUN`;
const short = (address: string) => `${address.slice(0, 4)}…${address.slice(-4)}`;
const poolName = (pool: Pick<OwnPool, "label" | "address">) => pool.label ?? short(pool.address);

function AlertList({ alerts }: { alerts: OwnSupplyAlert[] }) {
  if (alerts.length === 0) return <Note>No open alert: reclaims on time, bandwidth enough, windows in range.</Note>;
  return (
    <div class="mb-3 space-y-2">
      {alerts.map((alert, i) => (
        <Note key={`${alert.kind}-${i}`} tone={alert.level === "bad" ? "danger" : "warning"}>
          {alert.pool === null ? "" : `${short(alert.pool)}: `}
          {alert.message}
          {alert.since === null ? "" : ` Since ${utc(alert.since)}.`}
        </Note>
      ))}
    </div>
  );
}

/** Fill (line) over 24 h, one point per 10 minutes. */
function FillChart({ points }: { points: OwnPool["fill_24h"] }) {
  if (points.length < 2) return <p class="text-[11.5px] text-ink-muted">The 24-hour chart fills in as the worker takes a snapshot every minute.</p>;
  const w = 600;
  const h = 80;
  const t0 = Date.parse(points[0]!.at);
  const t1 = Date.parse(points.at(-1)!.at);
  const span = Math.max(1, t1 - t0);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${(((Date.parse(p.at) - t0) / span) * w).toFixed(1)},${(h - (p.fill ?? 0) * h).toFixed(1)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} class="h-20 w-full" role="img" aria-label="Fill over 24 hours">
      <path d={path} fill="none" stroke="currentColor" stroke-width="1.5" class="text-accent" />
    </svg>
  );
}

function useOwnSupply() {
  return useResource((signal) => {
    void signal;
    return adminReads.ownSupply();
  }, []);
}

/** 1. Overview: every pool's tiles and the alerts, per network (the Nile tab reads the Nile API). */
export function OwnSupplyOverview({ onPool }: { onPool: (address: string) => void }) {
  const data = useOwnSupply();
  const view = data.data ?? null;
  if (data.loading && view === null) return <Spinner />;
  if (data.error !== null && view === null) return <ErrorNote error={data.error} onRetry={() => data.reload()} />;
  if (view === null) return null;
  if (view.pools.length === 0) {
    return (
      <EmptyState>
        No own pool on this network yet. When a staked wallet is registered as a fund wallet, its stake, fill, recovery
        window and earnings show here.
      </EmptyState>
    );
  }
  return (
    <>
      <AlertList alerts={view.alerts} />
      {view.pools.map((pool) => (
        <PoolTiles key={pool.address} pool={pool} view={view} onOpen={() => onPool(pool.address)} />
      ))}
    </>
  );
}

function PoolTiles({ pool, view, onOpen }: { pool: OwnPool; view: OwnSupplyView; onOpen?: () => void }) {
  const worst = view.alerts.filter((a) => a.pool === pool.address || a.pool === null);
  const health = worst.some((a) => a.level === "bad") ? "act now" : worst.length > 0 ? "attention" : "fine";
  const w = pool.window;
  return (
    <Panel
      title={poolName(pool)}
      subtitle={`${pool.address} · snapshot ${utc(pool.snapshot_at)}${pool.snapshot_block === null ? "" : ` · block ${count(pool.snapshot_block)}`}`}
      action={
        <div class="flex items-center gap-2">
          <Pill value={health} tone={health === "fine" ? "good" : health === "attention" ? "attention" : "bad"} />
          {onOpen === undefined ? null : (
            <Button tone="ghost" onClick={onOpen}>
              Open pool
            </Button>
          )}
        </div>
      }
      class="mb-3"
    >
      <StatRow>
        <Tile
          label="Stake"
          value={pool.stake.energy_sun === null ? null : trx(pool.stake.energy_sun, 0)}
          unit="for energy"
          note={`${trx(pool.stake.bandwidth_sun, 0)} for bandwidth. Gives ${count(pool.energy.limit_per_day)} energy a day.`}
        />
        <Tile
          label="Fill"
          value={pct(pool.energy.fill_now)}
          note={`Share of the energy stake delegated out now (${trx(pool.stake.energy_delegated_sun, 0)}).`}
        />
        <Tile label="Free now" value={pool.energy.free_now === null ? null : count(pool.energy.free_now)} unit="energy" note="What the router can take on this pool this second." />
        <Tile
          label="Recovery window"
          value={hours(w.now_s)}
          note={`Median ${hours(w.median_24h_s) ?? "—"} over 24 h, ${hours(w.median_7d_s) ?? "—"} over 7 d; target ${hours(w.target_s)}.`}
          tone={w.now_s !== null && w.now_s > w.target_s + 3 * 3600 ? "warning" : "neutral"}
        />
        <Tile
          label="Limits sold / day"
          value={pool.turnover.limits_sold_24h === null ? null : pool.turnover.limits_sold_24h.toFixed(2)}
          note={`Energy delegated in 24 h ÷ daily limit. At saturation 24 ÷ window = ${pool.turnover.limits_theoretical === null ? "—" : pool.turnover.limits_theoretical.toFixed(2)}.`}
        />
        <Tile label="Revenue" value={trx(pool.money.revenue_24h_sun, 2)} unit="24 h" note={`${trx(pool.money.revenue_7d_sun, 2)} in 7 days, from orders filled on this stake.`} />
        <Tile
          label="Cost price"
          value={sunUnit(pool.money.cost_price_sun)}
          unit="per unit"
          note={
            pool.money.supplier_rate_centi_sun === null
              ? "Set this pool's supplier rate on its screen to see what a unit costs us."
              : "The pool's supplier rate ÷ limits sold a day (7-day average)."
          }
        />
        <Tile label="Realised price" value={sunUnit(pool.money.realised_price_sun)} unit="per unit" note="7-day revenue ÷ energy sold." />
        <Tile
          label="Yield"
          value={pct(pool.money.yield_gross)}
          unit="a year, gross"
          note={`Net of TRX burned on delegate and reclaim, on the whole stake: ${pct(pool.money.yield_net) ?? "—"}. Suppliers earn about 13 %.`}
        />
      </StatRow>
      <FillChart points={pool.fill_24h} />
    </Panel>
  );
}

/** 2. One pool: everything about it, its permissions (read-only) and its settings. */
export function OwnPoolScreen({ address, role, onBack }: { address: string; role: AdminRole; onBack: () => void }) {
  const detail = useResource((signal) => {
    void signal;
    return adminReads.ownPool(address);
  }, [address]);
  const view = detail.data ?? null;
  if (detail.loading && view === null) return <Spinner />;
  if (detail.error !== null && view === null) return <ErrorNote error={detail.error} onRetry={() => detail.reload()} />;
  if (view === null) return null;
  const pool = view.pool;
  return (
    <>
      <div class="mb-2">
        <Button tone="ghost" onClick={onBack}>
          ← All pools
        </Button>
      </div>
      <AlertList alerts={view.alerts} />
      <PoolTiles pool={pool} view={{ as_of: view.as_of, pools: [pool], reclaims: { open: 0, due_1h: 0, overdue: 0, oldest_overdue_at: null, overdue_after_s: 6 }, alerts: view.alerts }} />
      <div class="grid gap-3 xl:grid-cols-2">
        <Panel title="Meter" subtitle="The pool's own energy meter, from the latest chain snapshot.">
          <dl class="grid gap-x-6 sm:grid-cols-2">
            <Fact label="Daily limit L" value={count(pool.energy.limit_per_day)} mono />
            <Fact label="Used U (recovering)" value={count(pool.energy.used)} mono />
            <Fact label="Free now" value={count(pool.energy.free_now)} mono />
            <Fact label="Energy per TRX" value={pool.energy.energy_per_trx === null ? "—" : pool.energy.energy_per_trx.toFixed(3)} mono />
            <Fact label="Chain recovery window" value={hours(pool.window.chain_window_s) ?? "—"} mono />
            <Fact label="Window left now" value={hours(pool.window.now_s) ?? "—"} mono />
            <Fact label="Bandwidth staked" value={trx(pool.bandwidth.staked_sun, 0)} mono />
            <Fact label="Bandwidth left today" value={`${count(pool.bandwidth.left_now)} of ${count(pool.bandwidth.daily_budget)}`} mono />
          </dl>
          <p class="mt-2 text-[11px] text-ink-muted">
            The chain keeps one merged recovery window per account, so how much spent energy recovers in 0–6 / 6–12 /
            12–18 / 18–24 h is not shown: it cannot be read exactly.
          </p>
        </Panel>
        <PermissionsPanel permissions={view.permissions} />
      </div>
      <SettingsPanel address={address} role={role} settings={view.settings} onSaved={() => detail.reload()} />
    </>
  );
}

function PermissionsPanel({ permissions }: { permissions: Record<string, unknown> | null }) {
  const active = permissions === null ? [] : ((permissions["active"] as Array<Record<string, unknown>> | null) ?? []);
  const owner = permissions === null ? null : (permissions["owner"] as Record<string, unknown> | null);
  const keysOf = (p: Record<string, unknown> | null) =>
    p === null ? [] : ((p["keys"] as Array<{ address?: string; weight?: number }> | undefined) ?? []);
  return (
    <Panel title="Permissions" subtitle="Who may sign for this wallet, as the chain lists it. Read-only: never changed here.">
      {permissions === null ? (
        <p class="text-[12px] text-ink-muted">Not read yet: the worker records the list with its first snapshot.</p>
      ) : (
        <ul class="space-y-2 text-[12px]">
          {[{ name: "owner", p: owner }, ...active.map((p) => ({ name: String(p["permission_name"] ?? "active"), p }))].map(({ name, p }) => (
            <li key={name}>
              <span class="font-semibold">{name}</span>
              <span class="text-ink-muted"> · threshold {String(p?.["threshold"] ?? "—")}</span>
              <div class="num text-[11px] text-ink-muted">
                {keysOf(p).map((k) => `${k.address === undefined ? "?" : short(k.address)} (weight ${k.weight ?? "?"})`).join(", ") || "no keys listed"}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function SettingsPanel({
  address,
  role,
  settings,
  onSaved,
}: {
  address: string;
  role: AdminRole;
  settings: OwnPoolSettings;
  onSaved: () => void;
}) {
  const [windowHours, setWindowHours] = useState(String(settings.target_window_minutes / 60));
  const [rate, setRate] = useState(settings.supplier_rate_centi_sun === null ? "" : String(settings.supplier_rate_centi_sun / 100));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editor = role === "editor";
  const save = async () => {
    const minutes = Math.round(Number(windowHours) * 60);
    const rateCenti = rate.trim() === "" ? null : Math.round(Number(rate) * 100);
    if (!Number.isFinite(minutes) || minutes < 60 || minutes > 1440) return setError("The target window is between 1 and 24 hours.");
    if (rateCenti !== null && (!Number.isFinite(rateCenti) || rateCenti < 0)) return setError("The rate is a number of SUN, 0 or more.");
    setBusy(true);
    setError(null);
    try {
      await adminWrites.updateOwnPoolSettings(address, {
        target_window_minutes: minutes,
        supplier_rate_centi_sun: rateCenti,
        ...(reason.trim() === "" ? {} : { reason: reason.trim() }),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Not saved");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel
      title="Settings of this pool"
      subtitle={`Saved in the audit log. Settings only — nothing here acts on the chain.${settings.updated_at === null ? "" : ` Last change ${utc(settings.updated_at)}${settings.updated_by === null ? "" : ` by ${short(settings.updated_by)}`}.`}`}
      class="mt-3"
    >
      {editor ? (
        <div class="grid gap-3 sm:grid-cols-3">
          <Field label="Target recovery window, hours" hint="The window this pool aims for; the drift alert compares against it.">
            <input class={inputClass} inputMode="decimal" value={windowHours} onInput={(e) => setWindowHours((e.target as HTMLInputElement).value)} />
          </Field>
          <Field label="Supplier rate, SUN per energy unit a day" hint="What a unit of this pool's daily limit costs us. One flat number until the rate schedule exists.">
            <input class={inputClass} inputMode="decimal" placeholder="not set" value={rate} onInput={(e) => setRate((e.target as HTMLInputElement).value)} />
          </Field>
          <Field label="Reason" error={error}>
            <input class={inputClass} value={reason} onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
          </Field>
          <div class="sm:col-span-3">
            <Button disabled={busy} onClick={() => void save()}>
              Save settings
            </Button>
          </div>
        </div>
      ) : (
        <dl class="grid gap-x-6 sm:grid-cols-2">
          <Fact label="Target recovery window" value={hours(settings.target_window_minutes * 60) ?? "—"} mono />
          <Fact label="Supplier rate" value={settings.supplier_rate_centi_sun === null ? "not set" : `${settings.supplier_rate_centi_sun / 100} SUN per unit a day`} mono />
        </dl>
      )}
    </Panel>
  );
}

const STATUS_TONE = { active: "good", due_1h: "attention", overdue: "bad", reclaimed: "neutral" } as const;
const STATUS_TEXT = { active: "active", due_1h: "due ≤ 1 h", overdue: "overdue", reclaimed: "reclaimed" } as const;

/** 3. Delegations and reclaims. */
export function OwnDelegationsScreen({ onOrder }: { onOrder?: (orderId: string) => void }) {
  const [state, setState] = useState<"active" | "all">("active");
  const data = useResource((signal) => {
    void signal;
    return adminReads.ownDelegations(state);
  }, [state]);
  const view = data.data ?? null;
  if (data.loading && view === null) return <Spinner />;
  if (data.error !== null && view === null) return <ErrorNote error={data.error} onRetry={() => data.reload()} />;
  if (view === null) return null;
  const t = view.totals;
  const r = view.reclaims;
  return (
    <>
      <StatRow>
        <Tile label="Out now" value={count(t.active)} unit="delegations" note={`${count(t.active_energy)} energy with receivers.`} />
        <Tile label="Mean size" value={t.mean_amount === null ? null : count(t.mean_amount)} note={`Mean time held ${hours(t.mean_held_s) ?? "—"}.`} />
        <Tile label="Refills, 7 d" value={pct(t.refill_share_7d)} note="Share of delegations reclaimed within a minute." />
        <Tile
          label="Reclaims"
          value={count(r.due_1h)}
          unit="due ≤ 1 h"
          tone={r.overdue > 0 ? "danger" : "neutral"}
          note={r.overdue === 0 ? "None overdue." : `${count(r.overdue)} overdue (more than two blocks), oldest since ${utc(r.oldest_overdue_at)}.`}
        />
      </StatRow>
      <Panel
        title="Delegations"
        subtitle="From our own stake, newest first."
        action={
          <div class="flex gap-1">
            <Button tone={state === "active" ? "primary" : "ghost"} onClick={() => setState("active")}>
              Out now
            </Button>
            <Button tone={state === "all" ? "primary" : "ghost"} onClick={() => setState("all")}>
              Including reclaimed
            </Button>
          </div>
        }
      >
        <Table
          rows={view.data.length}
          empty="No delegation from our own stake."
          head={
            <tr>
              <Th>Order</Th>
              <Th>Receiver</Th>
              <Th numeric>Amount</Th>
              <Th>Term</Th>
              <Th>Delegated</Th>
              <Th>Reclaim due</Th>
              <Th>State</Th>
              <Th numeric>Burned by receiver</Th>
            </tr>
          }
        >
          {view.data.map((d) => (
            <tr key={d.allocation_id}>
              <Td mono>
                {onOrder === undefined ? (
                  <ShortId value={d.order_id} />
                ) : (
                  <button type="button" class="underline" onClick={() => onOrder(d.order_id)}>
                    {d.order_id.slice(0, 12)}…
                  </button>
                )}
              </Td>
              <Td>
                <Address value={d.receiver} />
              </Td>
              <Td numeric>
                {count(d.amount)} {d.resource === "energy" ? "" : d.resource}
              </Td>
              <Td>{d.tier ?? "—"}</Td>
              <Td mono>{utc(d.delegated_at)}</Td>
              <Td mono>{utc(d.reclaim_due_at)}</Td>
              <Td>
                <Pill value={STATUS_TEXT[d.status]} tone={STATUS_TONE[d.status]} />
              </Td>
              <Td numeric>{d.consumed_energy === null ? <Blank title="Sampled just before the reclaim; fleet average only" /> : count(d.consumed_energy)}</Td>
            </tr>
          ))}
        </Table>
      </Panel>
    </>
  );
}

/** 4. Admission decisions: orders a pool could have taken whole, and where they went. */
export function OwnAdmissionScreen() {
  const [days, setDays] = useState(1);
  const data = useResource((signal) => {
    void signal;
    return adminReads.ownAdmission(days);
  }, [days]);
  const view = data.data ?? null;
  if (data.loading && view === null) return <Spinner />;
  if (data.error !== null && view === null) return <ErrorNote error={data.error} onRetry={() => data.reload()} />;
  if (view === null) return null;
  return (
    <>
      {view.inputs_recorded ? null : (
        <Note tone="info">
          The router does not weigh orders yet: it puts our own stake first whenever the pool can fill, so there is no
          score. What it saw at its first ranking (own stake free, the cheapest venue) is recorded from this release on;
          older orders show where they went and how much the pool had free at that minute.
        </Note>
      )}
      <StatRow>
        <Tile label="Orders seen" value={count(view.summary.orders)} note={`Energy orders while a pool had free energy, last ${days} day${days === 1 ? "" : "s"}.`} />
        <Tile label="Taken on own stake" value={pct(view.summary.taken_share)} />
        <Tile
          label="Could have taken"
          value={count(view.summary.missed)}
          tone={view.summary.missed > 0 ? "warning" : "neutral"}
          note={`${count(view.summary.missed_energy)} energy a venue filled while the pool had it free.`}
        />
      </StatRow>
      <Panel
        title="Orders"
        action={
          <div class="flex gap-1">
            {[1, 7, 30].map((d) => (
              <Button key={d} tone={days === d ? "primary" : "ghost"} onClick={() => setDays(d)}>
                {d} d
              </Button>
            ))}
          </div>
        }
      >
        <Table
          rows={view.data.length}
          empty="No energy order arrived while a pool had free energy."
          head={
            <tr>
              <Th>Time</Th>
              <Th>Order</Th>
              <Th numeric>Amount</Th>
              <Th>Went to</Th>
              <Th numeric>Free then</Th>
              <Th numeric>Router saw own</Th>
              <Th>Cheapest venue</Th>
              <Th numeric>Sold at</Th>
              <Th numeric>Venue cost</Th>
            </tr>
          }
        >
          {view.data.map((row) => (
            <tr key={row.order_id}>
              <Td mono>{utc(row.created_at)}</Td>
              <Td mono>
                <ShortId value={row.order_id} />
              </Td>
              <Td numeric>{count(row.amount)}</Td>
              <Td>
                <Pill
                  value={row.taken === "own" ? "own stake" : row.taken === "split" ? `split · ${row.venues ?? "venue"}` : (row.venues ?? "venue")}
                  tone={row.taken === "venue" && row.could_take_whole ? "attention" : row.taken === "venue" ? "neutral" : "good"}
                />
              </Td>
              <Td numeric>{row.free_energy_then === null ? <Blank /> : count(row.free_energy_then)}</Td>
              <Td numeric>{row.inputs === null ? <Blank title="Not recorded for this order" /> : count(row.inputs.own_available)}</Td>
              <Td>
                {row.inputs?.best_venue == null ? (
                  <Blank />
                ) : (
                  `${row.inputs.best_venue.source_id} · ${row.inputs.best_venue.unit_sun.toFixed(2)} SUN`
                )}
              </Td>
              <Td numeric>{row.unit_price_centi_sun === null ? <Blank /> : `${(row.unit_price_centi_sun / 100).toFixed(2)} SUN`}</Td>
              <Td numeric>{row.venue_cost_sun === 0 ? "—" : trx(row.venue_cost_sun)}</Td>
            </tr>
          ))}
        </Table>
      </Panel>
    </>
  );
}

/** 5. Economics over 7 or 30 days. */
export function OwnEconomicsScreen() {
  const [days, setDays] = useState<7 | 30>(7);
  const data = useResource((signal) => {
    void signal;
    return adminReads.ownEconomics(days);
  }, [days]);
  const view = data.data ?? null;
  if (data.loading && view === null) return <Spinner />;
  if (data.error !== null && view === null) return <ErrorNote error={data.error} onRetry={() => data.reload()} />;
  if (view === null) return null;
  const saved =
    view.venue_cost_of_same_energy_sun === null ? null : view.venue_cost_of_same_energy_sun - view.revenue_sun;
  return (
    <>
      <div class="mb-3 flex gap-1">
        {([7, 30] as const).map((d) => (
          <Button key={d} tone={days === d ? "primary" : "ghost"} onClick={() => setDays(d)}>
            {d} days
          </Button>
        ))}
      </div>
      <StatRow>
        <Tile label="Revenue" value={trx(view.revenue_sun, 2)} note={`${count(view.delegations)} delegations, ${count(view.energy_sold)} energy sold.`} />
        <Tile label="Realised price" value={sunUnit(view.realised_price_sun)} unit="per unit" />
        <Tile label="Cost price" value={sunUnit(view.cost_price_sun)} unit="per unit" note={view.cost_price_sun === null ? "Needs every pool's supplier rate." : "Supplier rate ÷ limits sold a day."} />
        <Tile label="Margin" value={sunUnit(view.margin_per_unit_sun)} unit="per unit" />
        <Tile label="Bandwidth per order" value={view.bandwidth_per_order === null ? null : count(Math.round(view.bandwidth_per_order))} note={`Delegate + reclaim, 566 expected. TRX burned: ${trx(view.fee_burned_sun, 3)}.`} />
        <Tile label="Yield gross" value={pct(view.yield_gross)} unit="a year" note="On the energy stake." />
        <Tile label="Yield net" value={pct(view.yield_net)} unit="a year" note="Less TRX burned, on the whole stake." />
        <Tile
          label="Bought from venues instead"
          value={view.venue_cost_of_same_energy_sun === null ? null : trx(view.venue_cost_of_same_energy_sun, 2)}
          note={
            view.venue_unit_cost_sun === null
              ? "No venue fill in this window to compare with."
              : `At the venues' mean ${view.venue_unit_cost_sun.toFixed(2)} SUN per unit${saved === null ? "" : `; ${saved >= 0 ? "venues would cost" : "own stake earned"} ${trx(Math.abs(saved), 2)} ${saved >= 0 ? "more than our revenue" : "less"}`}.`
          }
        />
      </StatRow>
    </>
  );
}

/** 6. Bandwidth per pool: budget, today's use, pace. */
export function OwnBandwidthScreen() {
  const data = useOwnSupply();
  const view = data.data ?? null;
  if (data.loading && view === null) return <Spinner />;
  if (data.error !== null && view === null) return <ErrorNote error={data.error} onRetry={() => data.reload()} />;
  if (view === null) return null;
  const short_ = view.alerts.filter((a) => a.kind === "bandwidth_short");
  return (
    <>
      <AlertList alerts={short_} />
      <Panel title="Bandwidth" subtitle="Each delegate and reclaim burns about 282 + 284 bandwidth; the daily budget is the bandwidth stake plus 600 free.">
        <Table
          rows={view.pools.length}
          empty="No own pool on this network yet."
          head={
            <tr>
              <Th>Pool</Th>
              <Th numeric>Staked</Th>
              <Th numeric>Daily budget</Th>
              <Th numeric>Left now</Th>
              <Th numeric>Used 24 h</Th>
              <Th numeric>7-day pace / day</Th>
              <Th numeric>Budget ÷ pace</Th>
              <Th numeric>TRX burned 7 d</Th>
            </tr>
          }
        >
          {view.pools.map((p) => {
            const b = p.bandwidth;
            const cover = b.daily_budget === null || b.used_7d_per_day === 0 ? null : b.daily_budget / b.used_7d_per_day;
            return (
              <tr key={p.address}>
                <Td>{poolName(p)}</Td>
                <Td numeric>{trx(b.staked_sun, 0)}</Td>
                <Td numeric>{count(b.daily_budget)}</Td>
                <Td numeric>{count(b.left_now)}</Td>
                <Td numeric>{count(b.used_24h)}</Td>
                <Td numeric>{count(b.used_7d_per_day)}</Td>
                <Td numeric>{cover === null ? "—" : `${cover.toFixed(1)}×`}</Td>
                <Td numeric>{trx(b.fee_burned_7d_sun, 3)}</Td>
              </tr>
            );
          })}
        </Table>
      </Panel>
    </>
  );
}

/** 7. Suppliers: empty until the first supplier (owner decision 4). */
export function OwnSuppliersScreen() {
  return (
    <EmptyState>
      No supplier yet. When someone gives us stake (delegated in or by permission), this screen lists their wallets, stake
      type, rate schedule, energy sold from it, and the payout owed and paid.
    </EmptyState>
  );
}

/** The whole console behind one sub-tab strip: the Nile page's «Own supply» tab. */
export function OwnSupplyHub({ role }: { role: AdminRole }) {
  const [view, setView] = useState<OwnSupplyPage>("overview");
  const [pool, setPool] = useState<string | null>(null);
  return (
    <>
      <div class="mb-3 flex flex-wrap gap-1" role="tablist" aria-label="Own supply views">
        {OWN_SUPPLY_VIEWS.map((v) => (
          <Button
            key={v.view}
            tone={v.view === view ? "primary" : "ghost"}
            onClick={() => {
              setView(v.view);
              setPool(null);
            }}
          >
            {v.label}
          </Button>
        ))}
      </div>
      {view === "overview" ? (
        pool === null ? (
          <OwnSupplyOverview onPool={setPool} />
        ) : (
          <OwnPoolScreen address={pool} role={role} onBack={() => setPool(null)} />
        )
      ) : (
        <OwnSupplySection view={view} />
      )}
    </>
  );
}

export function OwnSupplySection({ view }: { view: Exclude<OwnSupplyPage, "overview"> }) {
  switch (view) {
    case "delegations":
      return <OwnDelegationsScreen />;
    case "admission":
      return <OwnAdmissionScreen />;
    case "economics":
      return <OwnEconomicsScreen />;
    case "bandwidth":
      return <OwnBandwidthScreen />;
    case "suppliers":
      return <OwnSuppliersScreen />;
  }
}

export const OWN_SUPPLY_LEADS: Record<OwnSupplyPage, { title: string; lead: string }> = {
  overview: { title: "Own supply", lead: "Our own stake as an energy provider: how full each pool is, how fast it recovers, what it earns. Watch-only." },
  delegations: { title: "Own delegations", lead: "What is out from our own stake, when it comes back, and what receivers burned." },
  admission: { title: "Admission", lead: "Orders a pool could have taken, and whether the router put them on our stake or on a venue." },
  economics: { title: "Own economics", lead: "Revenue, cost and yield of our own stake, against buying the same energy from venues." },
  bandwidth: { title: "Own bandwidth", lead: "The bandwidth our pools spend on delegate and reclaim, against their daily budget." },
  suppliers: { title: "Suppliers", lead: "People who give us stake, and what we owe them." },
};

export function OwnSupplyHeader({ view }: { view: OwnSupplyPage }) {
  return <ScreenHeader title={OWN_SUPPLY_LEADS[view].title} lead={OWN_SUPPLY_LEADS[view].lead} />;
}
